import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminLayout from './AdminLayout';
import SetupGuard from '../components/SetupGuard';
import { getFunctions, httpsCallable } from 'firebase/functions';
import {
  getFirestore,
  collection,
  query,
  orderBy,
  limit,
  startAfter,
  getDocs,
  doc,
  setDoc,
} from 'firebase/firestore';
import { getAuth } from 'firebase/auth';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import './FodyFlightPage.css';

// Typewriter hook: simulates AI typing character by character
function useTypewriter(fullText, speed = 12) {
  const [displayed, setDisplayed] = useState('');
  const indexRef = useRef(0);
  const fullRef = useRef(fullText);

  useEffect(() => {
    fullRef.current = fullText;
    indexRef.current = 0;
    setDisplayed('');
    if (!fullText) return;

    const interval = setInterval(() => {
      const next = indexRef.current + 1;
      if (next >= fullRef.current.length) {
        setDisplayed(fullRef.current);
        clearInterval(interval);
        return;
      }
      indexRef.current = next;
      setDisplayed(fullRef.current.slice(0, next));
    }, speed);

    return () => clearInterval(interval);
  }, [fullText, speed]);

  return displayed;
}

function TypewriterMarkdown({ text, speed = 12 }) {
  const displayed = useTypewriter(text, speed);
  return <ReactMarkdown remarkPlugins={[remarkGfm]}>{displayed}</ReactMarkdown>;
}

const functions = getFunctions();
const db = getFirestore();
const auth = getAuth();

const FodyFlightPage = () => {
  const navigate = useNavigate();
  const [messages, setMessages] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [activeSessionId, setActiveSessionId] = useState(null);
  const [input, setInput] = useState('');
  // Voice input (dictation) via the browser's Web Speech API — free in
  // Chrome/Edge/Safari, no backend involved: audio becomes text client-side
  // and the user reviews it in the input before sending (never auto-send).
  const [isListening, setIsListening] = useState(false);
  const recognitionRef = useRef(null);
  // Dictation language — the Web Speech API recognizes ONE language per
  // session (no simultaneous bilingual mode exists). Most FodyFee merchants
  // dictate in Spanish, so default to es-US with a one-tap ES/EN toggle;
  // the choice persists per browser.
  const [voiceLang, setVoiceLang] = useState(() => {
    try {
      return localStorage.getItem('fodyFlightVoiceLang') || 'es-US';
    } catch { return 'es-US'; }
  });
  const speechSupported = typeof window !== 'undefined'
    && (window.SpeechRecognition || window.webkitSpeechRecognition);
  const [loading, setLoading] = useState(false);
  const [sessionsLoading, setSessionsLoading] = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [requiresConfirmation, setRequiresConfirmation] = useState(false);
  const [hasMoreSessions, setHasMoreSessions] = useState(true);
  const [lastSessionDoc, setLastSessionDoc] = useState(null);
  const [hasMoreMessages, setHasMoreMessages] = useState(false);
  const [firstMessageDoc, setFirstMessageDoc] = useState(null);
  const [loadingMoreMessages, setLoadingMoreMessages] = useState(false);
  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const shouldScrollToBottom = useRef(true);
  const isFillingMessages = useRef(false);
  const hasMoreMessagesRef = useRef(hasMoreMessages);
  const loadingMoreMessagesRef = useRef(loadingMoreMessages);
  const activeSessionIdRef = useRef(activeSessionId);
  const firstMessageDocRef = useRef(firstMessageDoc);

  useEffect(() => {
    hasMoreMessagesRef.current = hasMoreMessages;
  }, [hasMoreMessages]);

  useEffect(() => {
    loadingMoreMessagesRef.current = loadingMoreMessages;
  }, [loadingMoreMessages]);

  useEffect(() => {
    activeSessionIdRef.current = activeSessionId;
  }, [activeSessionId]);

  useEffect(() => {
    firstMessageDocRef.current = firstMessageDoc;
  }, [firstMessageDoc]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    if (shouldScrollToBottom.current) {
      scrollToBottom();
    }
  }, [messages]);

  // Load initial sessions (20)
  useEffect(() => {
    const unsubscribeAuth = auth.onAuthStateChanged(async (user) => {
      if (!user) {
        setSessionsLoading(false);
        return;
      }
      try {
        const sessionsRef = collection(db, 'users', user.uid, 'fodySessions');
        const q = query(sessionsRef, orderBy('updatedAt', 'desc'), limit(20));
        const snapshot = await getDocs(q);
        const items = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setSessions(items);
        setLastSessionDoc(snapshot.docs[snapshot.docs.length - 1] || null);
        setHasMoreSessions(snapshot.docs.length === 20);
      } catch (err) {
        console.error('Sessions load error:', err);
      } finally {
        setSessionsLoading(false);
      }
    });

    return () => unsubscribeAuth();
  }, []);

  // Load more sessions (+10)
  const loadMoreSessions = async () => {
    const user = auth.currentUser;
    if (!user || !lastSessionDoc || !hasMoreSessions) return;

    setSessionsLoading(true);
    try {
      const sessionsRef = collection(db, 'users', user.uid, 'fodySessions');
      const q = query(
        sessionsRef,
        orderBy('updatedAt', 'desc'),
        startAfter(lastSessionDoc),
        limit(10)
      );
      const snapshot = await getDocs(q);
      const items = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setSessions((prev) => [...prev, ...items]);
      setLastSessionDoc(snapshot.docs[snapshot.docs.length - 1] || lastSessionDoc);
      setHasMoreSessions(snapshot.docs.length === 10);
    } catch (err) {
      console.error('Load more sessions error:', err);
    } finally {
      setSessionsLoading(false);
    }
  };

  // Load latest messages when session changes (last 10)
  const loadSessionMessages = async (sessionId) => {
    const user = auth.currentUser;
    if (!user) return;

    setActiveSessionId(sessionId);
    activeSessionIdRef.current = sessionId;
    setMessages([]);
    setLoading(true);
    setHasMoreMessages(false);
    hasMoreMessagesRef.current = false;
    setFirstMessageDoc(null);
    firstMessageDocRef.current = null;
    shouldScrollToBottom.current = true;

    try {
      const messagesRef = collection(
        db,
        'users',
        user.uid,
        'fodySessions',
        sessionId,
        'messages'
      );
      const q = query(messagesRef, orderBy('timestamp', 'desc'), limit(10));
      const snapshot = await getDocs(q);

      const msgs = snapshot.docs.map((doc) => {
        const data = doc.data();
        return {
          id: doc.id,
          role: data.role,
          text: data.text,
          timestamp: data.timestamp,
        };
      }).reverse();

      const firstDoc = snapshot.docs[snapshot.docs.length - 1] || null;
      setMessages(msgs);
      setHasMoreMessages(snapshot.docs.length === 10);
      hasMoreMessagesRef.current = snapshot.docs.length === 10;
      setFirstMessageDoc(firstDoc);
      firstMessageDocRef.current = firstDoc;
    } catch (e) {
      console.error('Error loading messages:', e);
      setMessages([
        {
          role: 'assistant',
          text: 'Sorry, I could not load this conversation. Please try again.',
        },
      ]);
    } finally {
      setLoading(false);
      fillUntilScrollable();
    }
  };

  // Load older messages (+10) when user scrolls up
  const loadOlderMessages = async () => {
    const user = auth.currentUser;
    const sessionId = activeSessionIdRef.current;
    const firstDoc = firstMessageDocRef.current;
    if (!user || !sessionId || !firstDoc || !hasMoreMessagesRef.current || loadingMoreMessagesRef.current) {
      console.log('[loadOlderMessages] skipped', { user: !!user, sessionId, firstDoc: !!firstDoc, hasMoreMessages: hasMoreMessagesRef.current, loadingMoreMessages: loadingMoreMessagesRef.current });
      return;
    }

    setLoadingMoreMessages(true);
    loadingMoreMessagesRef.current = true;
    shouldScrollToBottom.current = false;

    const container = messagesContainerRef.current;
    const prevScrollHeight = container?.scrollHeight || 0;
    const prevScrollTop = container?.scrollTop || 0;

    try {
      const messagesRef = collection(
        db,
        'users',
        user.uid,
        'fodySessions',
        sessionId,
        'messages'
      );
      const q = query(
        messagesRef,
        orderBy('timestamp', 'desc'),
        startAfter(firstDoc),
        limit(10)
      );
      const snapshot = await getDocs(q);

      const olderMsgs = snapshot.docs.map((doc) => {
        const data = doc.data();
        return {
          id: doc.id,
          role: data.role,
          text: data.text,
          timestamp: data.timestamp,
        };
      }).reverse();

      const newFirstDoc = snapshot.docs[snapshot.docs.length - 1] || firstMessageDocRef.current;
      setMessages((prev) => [...olderMsgs, ...prev]);
      setHasMoreMessages(snapshot.docs.length === 10);
      setFirstMessageDoc(newFirstDoc);
      firstMessageDocRef.current = newFirstDoc;

      // Restore scroll position after new messages are prepended
      requestAnimationFrame(() => {
        if (container) {
          const newScrollHeight = container.scrollHeight;
          container.scrollTop = newScrollHeight - prevScrollHeight + prevScrollTop;
        }
        fillUntilScrollable();
      });
    } catch (e) {
      console.error('Error loading older messages:', e);
    } finally {
      setLoadingMoreMessages(false);
      loadingMoreMessagesRef.current = false;
    }
  };

  const handleMessagesScroll = (e) => {
    const container = e.currentTarget;
    if (container.scrollTop < 50 && hasMoreMessagesRef.current && !loadingMoreMessagesRef.current) {
      loadOlderMessages();
    }
  };

  // If the messages container is not scrollable and there are more messages,
  // keep loading older messages until it becomes scrollable or no more exist.
  const fillUntilScrollable = async () => {
    if (isFillingMessages.current) return;
    isFillingMessages.current = true;

    try {
      // eslint-disable-next-line no-constant-condition
      while (true) {
        const container = messagesContainerRef.current;
        if (!container) {
          console.log('[fillUntilScrollable] no container');
          break;
        }

        // Wait for React to paint the latest messages and commit state updates
        await new Promise((resolve) => requestAnimationFrame(resolve));
        await new Promise((resolve) => setTimeout(resolve, 0));

        const hasScroll = container.scrollHeight > container.clientHeight + 1;
        console.log('[fillUntilScrollable] scrollHeight:', container.scrollHeight, 'clientHeight:', container.clientHeight, 'hasScroll:', hasScroll, 'hasMoreMessages:', hasMoreMessagesRef.current, 'loadingMoreMessages:', loadingMoreMessagesRef.current);
        if (hasScroll) break;
        if (!hasMoreMessagesRef.current || loadingMoreMessagesRef.current) break;

        console.log('[fillUntilScrollable] loading older messages');
        await loadOlderMessages();
      }
    } catch (e) {
      console.error('Error filling messages until scrollable:', e);
    } finally {
      isFillingMessages.current = false;
    }
  };

  const startNewChat = () => {
    setActiveSessionId(null);
    activeSessionIdRef.current = null;
    setRequiresConfirmation(false);
    setHasMoreMessages(false);
    hasMoreMessagesRef.current = false;
    setFirstMessageDoc(null);
    firstMessageDocRef.current = null;
    shouldScrollToBottom.current = true;
    setMessages([
      {
        role: 'assistant',
        text: "Hi! I'm Fody Flight, your AI assistant. Ask me about sales reports, restaurant suggestions, or business insights.",
      },
    ]);
  };

  const markFodyFlightUsed = async () => {
    try {
      const auth = getAuth();
      const user = auth.currentUser;
      if (!user) return;
      const db = getFirestore();
      await setDoc(doc(db, 'users', user.uid), { fodyFlightEnabled: true }, { merge: true });
      window.dispatchEvent(new CustomEvent('setupGuideRefresh'));
    } catch (e) {
      console.error('[FodyFlight] Error marking fodyFlightEnabled:', e);
    }
  };

  // Mic toggle: starts/stops a dictation session. Final + interim results
  // stream into the input box (appended to whatever was already typed).
  const toggleVoice = () => {
    if (isListening) {
      recognitionRef.current?.stop();
      return;
    }
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) return;
    const rec = new SR();
    recognitionRef.current = rec;
    rec.lang = voiceLang; // user-picked ES/EN (see toggle next to the mic)
    rec.continuous = true;
    rec.interimResults = true;

    const base = input; // append dictation to existing text
    let sessionFinal = '';
    rec.onresult = (event) => {
      let interim = '';
      for (let i = 0; i < event.results.length; i++) {
        const t = event.results[i][0].transcript;
        if (event.results[i].isFinal) sessionFinal += t + ' ';
        else interim += t;
      }
      const combined = ((base ? base + ' ' : '') + sessionFinal + interim).replace(/\s+/g, ' ');
      setInput(combined.trimStart());
    };
    rec.onend = () => setIsListening(false);
    rec.onerror = (e) => {
      console.warn('[FodyFlight] speech recognition error:', e.error);
      setIsListening(false);
    };
    rec.start();
    setIsListening(true);
  };

  const handleSend = async () => {
    if (!input.trim() || loading) return;
    if (isListening) {
      recognitionRef.current?.stop();
      setIsListening(false);
    }

    const userMsg = input.trim();
    const now = new Date();
    shouldScrollToBottom.current = true;
    setMessages((prev) => [...prev, { role: 'user', text: userMsg, timestamp: now }]);
    setInput('');
    setLoading(true);

    try {
      const askAI = httpsCallable(functions, 'fodyFlightAsk');
      const timezoneOffset = new Date().getTimezoneOffset();
      const result = await askAI({ prompt: userMsg, sessionId: activeSessionId, timezoneOffset });
      const responseText = result.data?.response || "I'm still learning. Please try again later.";
      const returnedSessionId = result.data?.sessionId;
      const needsConfirm = result.data?.requiresConfirmation || false;

      if (returnedSessionId && !activeSessionId) {
        setActiveSessionId(returnedSessionId);
      }

      setRequiresConfirmation(needsConfirm);
      setMessages((prev) => [...prev, { role: 'assistant', text: responseText, timestamp: new Date() }]);
      await markFodyFlightUsed();
    } catch (e) {
      console.error('Fody Flight error:', e);
      setMessages((prev) => [
        ...prev,
        { role: 'assistant', text: "Sorry, I'm having trouble connecting right now. Please try again.", timestamp: new Date() },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const handleConfirm = (confirmed) => async () => {
    const text = confirmed ? 'Yes, I agree' : 'No, cancel';
    const now = new Date();
    shouldScrollToBottom.current = true;
    setMessages((prev) => [...prev, { role: 'user', text, timestamp: now }]);
    setRequiresConfirmation(false);
    setLoading(true);

    try {
      const askAI = httpsCallable(functions, 'fodyFlightAsk');
      const timezoneOffset = new Date().getTimezoneOffset();
      const result = await askAI({ prompt: text, sessionId: activeSessionId, timezoneOffset });
      const responseText = result.data?.response || "I'm still learning. Please try again later.";
      const needsConfirm = result.data?.requiresConfirmation || false;

      setRequiresConfirmation(needsConfirm);
      setMessages((prev) => [...prev, { role: 'assistant', text: responseText, timestamp: new Date() }]);
      await markFodyFlightUsed();
    } catch (e) {
      console.error('Fody Flight error:', e);
      setMessages((prev) => [
        ...prev,
        { role: 'assistant', text: "Sorry, I'm having trouble connecting right now. Please try again.", timestamp: new Date() },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const quickPrompts = [
    'How were my sales last week?',
    'What products should I promote?',
    "Summarize today's orders",
    'Tips to increase revenue',
  ];

  const formatDate = (timestamp) => {
    if (!timestamp) return '';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const formatMessageTime = (timestamp) => {
    if (!timestamp) return '';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <AdminLayout title="Fody Flight" subtitle="AI-Powered Business Assistant">
      <SetupGuard prerequisites={[]}>
      <div className="ff-flight-wrapper">
        {/* Sidebar */}
        <div className={`ff-flight-sidebar ${sidebarOpen ? 'open' : ''}`}>
          <div className="ff-flight-sidebar-header">
            <button className="ff-flight-new-chat-btn" onClick={startNewChat}>
              <i className="fas fa-plus"></i>
              New Chat
            </button>
          </div>
          <div className="ff-flight-sidebar-list">
            {sessionsLoading ? (
              <div className="ff-flight-sidebar-loading">
                <i className="fas fa-spinner fa-spin"></i>
                <span>Loading chats...</span>
              </div>
            ) : sessions.length === 0 ? (
              <div className="ff-flight-sidebar-empty">
                <i className="fas fa-comments"></i>
                <span>No chats yet</span>
              </div>
            ) : (
              sessions.map((session) => (
                <div
                  key={session.id}
                  className={`ff-flight-sidebar-item ${activeSessionId === session.id ? 'active' : ''}`}
                  onClick={() => {
                    loadSessionMessages(session.id);
                    setSidebarOpen(false);
                  }}
                >
                  <i className="fas fa-comment"></i>
                  <div className="ff-flight-sidebar-item-text">
                    <span className="title">{session.title || 'Untitled chat'}</span>
                    <span className="date">{formatDate(session.updatedAt)}</span>
                  </div>
                </div>
              ))
            )}
            {hasMoreSessions && (
              <button
                className="ff-flight-load-more-btn"
                onClick={loadMoreSessions}
                disabled={sessionsLoading}
              >
                {sessionsLoading ? (
                  <><i className="fas fa-spinner fa-spin"></i> Loading...</>
                ) : (
                  <><i className="fas fa-chevron-down"></i> Load More</>
                )}
              </button>
            )}
          </div>
        </div>

        {/* Main chat area */}
        <div className="ff-flight-container">
          {/* Mobile sidebar toggle */}
          <div className="ff-flight-mobile-header">
            <button
              className="ff-flight-menu-btn"
              onClick={() => setSidebarOpen(!sidebarOpen)}
            >
              <i className="fas fa-bars"></i>
            </button>
            <div className="ff-flight-header-center">
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
                <h3 style={{ margin: 0, fontSize: '1.1rem', fontWeight: 700 }}>Fody Flight</h3>
                <span
                  style={{
                    fontSize: '0.65rem',
                    fontWeight: 700,
                    color: '#FF5722',
                    background: 'rgba(255, 87, 34, 0.12)',
                    padding: '2px 8px',
                    borderRadius: 10,
                    border: '1px solid rgba(255, 87, 34, 0.25)',
                  }}
                >
                  AI
                </span>
                <span
                  style={{
                    fontSize: '0.6rem',
                    fontWeight: 700,
                    color: '#FF5722',
                    background: 'rgba(255, 87, 34, 0.08)',
                    padding: '2px 6px',
                    borderRadius: 6,
                    border: '1px solid rgba(255, 87, 34, 0.2)',
                    textTransform: 'uppercase',
                  }}
                >
                  BETA
                </span>
              </div>
              <p style={{ margin: 0, fontSize: '0.8rem', color: '#888' }}>
                {loading ? 'Thinking...' : 'Online'}
              </p>
            </div>
            <div style={{ width: 40 }} />
          </div>

          {/* Desktop header */}
          <div className="ff-flight-header desktop-only">
            <div className="ff-flight-avatar">
              <img src="/fodyAI.png" alt="Fody" style={{ width: 32, height: 32, objectFit: 'contain' }} />
            </div>
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <h3 style={{ margin: 0, fontSize: '1.1rem', fontWeight: 700 }}>Fody Flight</h3>
                <span
                  style={{
                    fontSize: '0.65rem',
                    fontWeight: 700,
                    color: '#FF5722',
                    background: 'rgba(255, 87, 34, 0.12)',
                    padding: '2px 8px',
                    borderRadius: 10,
                    border: '1px solid rgba(255, 87, 34, 0.25)',
                  }}
                >
                  AI
                </span>
                <span
                  style={{
                    fontSize: '0.6rem',
                    fontWeight: 700,
                    color: '#FF5722',
                    background: 'rgba(255, 87, 34, 0.08)',
                    padding: '2px 6px',
                    borderRadius: 6,
                    border: '1px solid rgba(255, 87, 34, 0.2)',
                    textTransform: 'uppercase',
                  }}
                >
                  BETA
                </span>
              </div>
              <p style={{ margin: 0, fontSize: '0.8rem', color: '#888' }}>
                {loading ? 'Thinking...' : 'Online'}
              </p>
            </div>
          </div>

          {/* Messages */}
          <div
            className="ff-flight-messages"
            ref={messagesContainerRef}
            onScroll={handleMessagesScroll}
          >
            {loadingMoreMessages && (
              <div className="ff-flight-message assistant">
                <div className="ff-flight-bubble">
                  <div className="ff-flight-bubble-avatar">
                    <img src="/fodyAI.png" alt="Fody" style={{ width: 20, height: 20, objectFit: 'contain' }} />
                  </div>
                  <div className="ff-flight-typing">
                    <span></span>
                    <span></span>
                    <span></span>
                  </div>
                </div>
              </div>
            )}
            {(() => {
              // Find the index of the last assistant message (the newest one)
              let lastAssistantIndex = -1;
              for (let i = messages.length - 1; i >= 0; i--) {
                if (messages[i].role === 'assistant') {
                  lastAssistantIndex = i;
                  break;
                }
              }
              return messages.map((msg, i) => (
                <div
                  key={i}
                  className={`ff-flight-message ${msg.role === 'user' ? 'user' : 'assistant'}`}
                >
                  <div className="ff-flight-bubble">
                    {msg.role === 'assistant' && (
                      <div className="ff-flight-bubble-avatar">
                        <img src="/fodyAI.png" alt="Fody" style={{ width: 20, height: 20, objectFit: 'contain' }} />
                      </div>
                    )}
                    <div className="ff-flight-bubble-text">
                      {msg.role === 'assistant' ? (
                        i === lastAssistantIndex ? (
                          <TypewriterMarkdown text={msg.text} speed={14} />
                        ) : (
                          <ReactMarkdown remarkPlugins={[remarkGfm]}>{msg.text}</ReactMarkdown>
                        )
                      ) : (
                        msg.text
                      )}
                    </div>
                    {msg.timestamp && (
                      <div style={{ fontSize: '0.7rem', color: '#aaa', marginTop: 4, textAlign: msg.role === 'user' ? 'right' : 'left' }}>
                        {formatMessageTime(msg.timestamp)}
                      </div>
                    )}
                  </div>
                </div>
              ));
            })()}
            {loading && (
              <div className="ff-flight-message assistant">
                <div className="ff-flight-bubble">
                  <div className="ff-flight-bubble-avatar">
                    <img src="/fodyAI.png" alt="Fody" style={{ width: 20, height: 20, objectFit: 'contain' }} />
                  </div>
                  <div className="ff-flight-typing">
                    <span></span>
                    <span></span>
                    <span></span>
                  </div>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Quick Prompts */}
          {messages.length <= 2 && (
            <div className="ff-flight-quick-prompts">
              {quickPrompts.map((prompt, i) => (
                <button
                  key={i}
                  className="ff-flight-prompt-chip"
                  onClick={() => {
                    setInput(prompt);
                  }}
                >
                  {prompt}
                </button>
              ))}
            </div>
          )}

          {/* Input / Confirmation */}
          {requiresConfirmation ? (
            <div className="ff-flight-input-area" style={{ justifyContent: 'center', gap: 12 }}>
              <button
                className="ff-flight-send-btn"
                style={{ width: 'auto', padding: '0 24px', borderRadius: 12 }}
                onClick={handleConfirm(true)}
              >
                <i className="fas fa-check" style={{ marginRight: 6 }}></i>
                Yes, Confirm
              </button>
              <button
                className="ff-flight-send-btn"
                style={{ width: 'auto', padding: '0 24px', borderRadius: 12, background: '#999' }}
                onClick={handleConfirm(false)}
              >
                <i className="fas fa-times" style={{ marginRight: 6 }}></i>
                No, Cancel
              </button>
            </div>
          ) : (
            <div className="ff-flight-input-area">
              <textarea
                className="ff-flight-input"
                rows={1}
                placeholder={isListening ? 'Listening… speak now' : 'Ask Fody Flight anything...'}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
              />
              {speechSupported && (
                <>
                  <button
                    className="ff-flight-send-btn"
                    style={{ background: isListening ? '#e53935' : '#9ca3af' }}
                    onClick={toggleVoice}
                    title={isListening ? 'Stop dictation' : 'Dictate to Fody Flight'}
                    type="button"
                  >
                    <i className={`fas ${isListening ? 'fa-microphone' : 'fa-microphone-slash'}`}></i>
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      if (isListening) return; // can't switch mid-session
                      const next = voiceLang === 'es-US' ? 'en-US' : 'es-US';
                      setVoiceLang(next);
                      try { localStorage.setItem('fodyFlightVoiceLang', next); } catch {}
                    }}
                    title={`Dictation language: ${voiceLang === 'es-US' ? 'Español' : 'English'} (tap to switch)`}
                    style={{
                      background: 'none', border: '1px solid #d1d5db', borderRadius: 8,
                      color: isListening ? '#d1d5db' : '#6b7280', fontWeight: 700, fontSize: 12,
                      padding: '0 8px', height: 34, cursor: isListening ? 'default' : 'pointer',
                    }}
                  >
                    {voiceLang === 'es-US' ? 'ES' : 'EN'}
                  </button>
                </>
              )}
              <button
                className="ff-flight-send-btn"
                onClick={handleSend}
                disabled={!input.trim() || loading}
              >
                <i className="fas fa-paper-plane"></i>
              </button>
            </div>
          )}
        </div>
      </div>
      </SetupGuard>
    </AdminLayout>
  );
};

export default FodyFlightPage;
